import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cloneElement, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import classNames from "classnames";
import { noop } from "lodash";
import debounce from "lodash/debounce";
import { offset, flip, shift, size as floatingSize, autoUpdate, useFloating, useInteractions, useRole, useClick, useDismiss, FloatingPortal, useListNavigation, FloatingFocusManager, useFocus, } from "@floating-ui/react";
import { ThemeProviderContext } from "../ThemeProvider";
import { ListboxOption } from "./ListboxOption";
import { Typography } from "../Typography";
import { Field } from "./field/Field";
import { usePrevious } from "../utils/usePrevious";
const defaultCompare = (a, b) => a === b;
export const Select = (outerProps) => {
    const props = Object.assign(Object.assign({}, outerProps), { isClearable: Boolean(outerProps.isClearable), isDisabled: Boolean(outerProps.isDisabled), size: outerProps.size || "medium", orientation: outerProps.orientation || "vertical", className: outerProps.className || "", onKeywordChange: outerProps.onKeywordChange || noop, compareBy: outerProps.compareBy || defaultCompare, backgroundStyle: outerProps.backgroundStyle || "transparent" });
    const isMultiple = props.selectionType === "multiple";
    const isRemote = !!outerProps.onKeywordChange;
    const hasTextInput = !!props.optionFilter || isRemote;
    const isEnabled = !props.isDisabled && !props.isReadonly;
    const [keywordFilter, setKeywordFilter] = useState("");
    const { options, optionFilter, value, compareBy } = props;
    const isSelectedOption = useCallback((option) => {
        if (isMultiple) {
            return !value.some((selectedOption) => compareBy(selectedOption.value, option.value));
        }
        else {
            return !compareBy(value === null || value === void 0 ? void 0 : value.value, option.value);
        }
    }, [isMultiple, compareBy, value]);
    const filteredOptions = useMemo(() => {
        if (isRemote) {
            return options.filter(isSelectedOption);
        }
        else {
            const filter = (option) => {
                if (!keywordFilter || !optionFilter) {
                    return true;
                }
                return optionFilter(keywordFilter, option);
            };
            return options.reduce((filtered, option) => {
                if (option.options) {
                    const filteredGroupedOptions = option.options.filter((groupedOption) => filter(groupedOption));
                    if (filteredGroupedOptions.length > 0) {
                        filtered.push(Object.assign(Object.assign({}, option), { options: filteredGroupedOptions }));
                    }
                }
                else {
                    if (filter(option)) {
                        filtered.push(option);
                    }
                }
                return filtered;
            }, []);
        }
    }, [keywordFilter, optionFilter, options, isRemote, isSelectedOption]);
    const listRef = useRef([]);
    const triggerRef = useRef(null);
    const flatten = useCallback((options) => options
        .map((option) => {
        if (option.options) {
            return option.options;
        }
        else {
            return option;
        }
    })
        .flat(), []);
    const flattenedOptions = useMemo(() => flatten(outerProps.options), [outerProps.options, flatten]);
    const selectedIndex = !isMultiple && !!value
        ? flattenedOptions === null || flattenedOptions === void 0 ? void 0 : flattenedOptions.indexOf(value)
        : null;
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(selectedIndex);
    const [hideActiveIndex, setHideActiveIndex] = useState(true);
    const [debouncedKeywordFilter, setDebouncedKeywordFilter] = useState("");
    const onOpenChange = outerProps.onOpenChange;
    useEffect(() => {
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(isOpen);
    }, [isOpen, onOpenChange]);
    const { reference, floating, context, strategy, x, y } = useFloating({
        whileElementsMounted: autoUpdate,
        open: isOpen,
        onOpenChange: (value) => {
            if (isRemote && (filteredOptions === null || filteredOptions === void 0 ? void 0 : filteredOptions.length) < 1) {
                return;
            }
            setIsOpen(value);
        },
        strategy: "fixed",
        middleware: [
            offset(8),
            flip(),
            shift(),
            floatingSize({
                apply: ({ elements, rects }) => {
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                    });
                },
            }),
        ],
    });
    const hasValue = (!isMultiple && !!value) ||
        (isMultiple && (value === null || value === void 0 ? void 0 : value.length) > 0);
    const onNavigate = (index) => {
        setActiveIndex(index !== null && index !== void 0 ? index : activeIndex);
        if (index === null) {
            setHideActiveIndex(true);
        }
        if (index !== null && index !== selectedIndex) {
            setHideActiveIndex(false);
        }
    };
    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useRole(context, { role: "listbox", enabled: isEnabled }),
        useDismiss(context, {
            enabled: isEnabled,
        }),
        useFocus(context, {
            enabled: isEnabled && hasTextInput,
            keyboardOnly: false,
        }),
        useClick(context, {
            enabled: isEnabled && !hasTextInput,
            toggle: true,
        }),
        useListNavigation(context, {
            enabled: isEnabled,
            listRef,
            activeIndex,
            onNavigate,
            loop: true,
            selectedIndex,
            focusItemOnHover: false,
            focusItemOnOpen: hasValue,
            virtual: hasTextInput,
        }),
    ]);
    useLayoutEffect(() => {
        requestAnimationFrame(() => {
            var _a;
            if (activeIndex != null) {
                (_a = listRef.current[activeIndex]) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: "nearest" });
            }
        });
    }, [activeIndex]);
    useLayoutEffect(() => {
        var _a;
        if (isOpen && !isMultiple && !!value) {
            (_a = listRef.current[flattenedOptions === null || flattenedOptions === void 0 ? void 0 : flattenedOptions.indexOf(value)]) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: "nearest" });
        }
    }, [isOpen, isMultiple, value, flattenedOptions]);
    const clearValue = () => {
        var _a, _b;
        if (isMultiple) {
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, []);
        }
        else {
            (_b = props.onChange) === null || _b === void 0 ? void 0 : _b.call(props, null);
        }
        setIsOpen(false);
    };
    const isPreviouslySelectedOption = (changedOption) => {
        var _a, _b;
        if (isMultiple) {
            return (_a = props.value) === null || _a === void 0 ? void 0 : _a.some((option) => {
                return compareBy(option.value, changedOption.value);
            });
        }
        else {
            return compareBy((_b = props.value) === null || _b === void 0 ? void 0 : _b.value, changedOption.value);
        }
    };
    const toggleOption = (changedOption) => {
        var _a, _b, _c, _d;
        if (!changedOption) {
            return;
        }
        if (isMultiple) {
            const options = isPreviouslySelectedOption(changedOption)
                ? value.filter((selectedOption) => !compareBy(selectedOption.value, changedOption.value))
                : [...value, changedOption];
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, options);
        }
        else {
            if (props.isClearable && isPreviouslySelectedOption(changedOption)) {
                (_b = props.onChange) === null || _b === void 0 ? void 0 : _b.call(props, null);
            }
            else {
                (_c = props.onChange) === null || _c === void 0 ? void 0 : _c.call(props, changedOption);
            }
            setIsOpen(false);
            (_d = triggerRef.current) === null || _d === void 0 ? void 0 : _d.focus();
        }
        setKeywordFilter("");
        setDebouncedKeywordFilter("");
    };
    const removeLastValue = () => {
        var _a;
        if (!isMultiple) {
            return;
        }
        const options = value.slice(0, -1);
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, options);
    };
    const close = () => {
        var _a;
        setIsOpen(false);
        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const renderListboxOption = (option, index) => {
        var _a, _b;
        const isActive = activeIndex === index && !hideActiveIndex;
        const isSelected = isMultiple
            ? (_a = props.value) === null || _a === void 0 ? void 0 : _a.some((o) => props.compareBy(o.value, option.value))
            : props.compareBy((_b = props.value) === null || _b === void 0 ? void 0 : _b.value, option.value);
        return (_jsx(ListboxOption, { option: option, isActive: isActive, closeListbox: close, isSelected: isSelected, isMultiple: isMultiple, hideCheckmark: isRemote, index: index, itemProps: getItemProps({
                ref(node) {
                    listRef.current[index] = node;
                },
            }), selectOption: toggleOption }, index));
    };
    const handleKeywordChangeDebounced = useRef(debounce((keyword) => {
        setDebouncedKeywordFilter(keyword);
        props.onKeywordChange(keyword);
    }, 250)).current;
    const handleKeywordChange = (keyword) => {
        setKeywordFilter(keyword);
        handleKeywordChangeDebounced(keyword);
    };
    const { isLoading } = props;
    const wasLoading = usePrevious(isLoading);
    useEffect(() => {
        if (wasLoading && !isLoading) {
            setIsOpen(true);
        }
    }, [wasLoading, isLoading]);
    let optionIndex = 0;
    const hasKeywordFilter = () => (!isRemote && !!keywordFilter) || (isRemote && !!debouncedKeywordFilter);
    const portalRoot = typeof document !== "undefined" ? document.body : null;
    return (_jsx(ThemeProviderContext.Consumer, { children: ({ menuZIndex, translate }) => (_jsxs(_Fragment, { children: [props.customTrigger ? (cloneElement(props.customTrigger, getReferenceProps({ ref: reference }))) : (_jsx(Field, Object.assign({}, props, { isDisabled: props.isDisabled, isReadonly: props.isReadonly, clearValue: clearValue, filteredOptions: filteredOptions, onSelectionChange: toggleOption, removeLastValue: removeLastValue, setKeywordFilter: handleKeywordChange, keywordFilter: keywordFilter, activeIndex: activeIndex, hasTextInput: hasTextInput, getReferenceProps: getReferenceProps, controlRef: reference, triggerRef: triggerRef, closeListbox: close, flattenedOptions: flattenedOptions, flatten: flatten, isMultiple: isMultiple, testId: props.testId }))), _jsx(FloatingPortal, { root: portalRoot, children: isOpen && (_jsx(FloatingFocusManager, { context: context, order: ["reference", "content"], children: _jsx("div", Object.assign({}, getFloatingProps({
                            className: classNames("rounded drop-shadow dark:drop-shadow-dark bg-background-primary dark:bg-background-primary-darkmode outline-none", props.menuClassName),
                            ref: floating,
                            style: {
                                position: strategy,
                                top: y !== null && y !== void 0 ? y : "",
                                left: x !== null && x !== void 0 ? x : "",
                                zIndex: menuZIndex,
                            },
                        }), { role: "listbox", children: _jsxs("ul", { className: "max-h-36 overflow-y-auto mb-0", children: [filteredOptions.map((option) => {
                                        if (option.options) {
                                            return (_jsxs(Fragment, { children: [_jsx("div", { className: "border-t border-t-border-subdued dark:border-t-border-subdued-darkmode first:border-0", style: { overflowWrap: "anywhere" }, children: option.label && (_jsx(Typography, { variant: "body-small-emphasis", color: "text-primary", className: "px-1 pt-0.75 pb-0.25", children: option.label })) }), option.options.map((groupOption) => renderListboxOption(groupOption, optionIndex++))] }, option.label));
                                        }
                                        else {
                                            return renderListboxOption(option, optionIndex++);
                                        }
                                    }), filteredOptions.length < 1 &&
                                        hasKeywordFilter() &&
                                        !props.isLoading && (_jsx(ListboxOption, { option: {
                                            label: translate("select.no_matching_options", {
                                                keywordFilter,
                                            }),
                                        }, closeListbox: close }))] }) })) })) })] })) }));
};
//# sourceMappingURL=Select.js.map